'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildSheetHtml,
  exportSheetToPdf,
  exportSlidesToPdf,
  exportWordBlocksToPdf,
  isDocxFile,
  isPptxFile,
  isXlsxFile,
  parseWordHtmlToBlocks,
  readDocxHtml,
  readPptxSlides,
  readWorkbookPreview,
  type PptxSlidePreview,
  type WorkbookSheetPreview,
} from '@/lib/browser-office-tools';
import {
  createCleanorExportFileName,
  formatFileSize,
  triggerBrowserDownload,
} from '@/lib/browser-pdf-tools';
import { getBaseName } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

export type OfficeToPdfVariant = 'word-to-pdf' | 'excel-to-pdf' | 'powerpoint-to-pdf';

type WordPreview = {
  html: string;
  messages: string[];
};

type ExcelPreview = {
  sheets: WorkbookSheetPreview[];
};

type PowerPointPreview = {
  slides: PptxSlidePreview[];
};

type ToolCopy = {
  title: string;
  subtitle: string;
  badge: string;
  inputLabel: string;
  inputHint: string;
  buttonLabel: string;
  processingLabel: string;
  successLabel: string;
  accept: string;
  validate: (file: File) => boolean;
};

const toolCopy: Record<OfficeToPdfVariant, ToolCopy> = {
  'word-to-pdf': {
    title: 'Convert DOCX to PDF in the browser',
    subtitle:
      'Load one Word document, preview the parsed content, and export a practical PDF locally.',
    badge: 'DOCX',
    inputLabel: 'Choose one DOCX file or drop it here',
    inputHint:
      'Supports practical text, headings, lists, tables, and embedded images when parseable',
    buttonLabel: 'Convert to PDF',
    processingLabel: 'Converting…',
    successLabel: 'Word-to-PDF conversion',
    accept: '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    validate: isDocxFile,
  },
  'excel-to-pdf': {
    title: 'Convert XLSX sheets to PDF',
    subtitle:
      'Pick a workbook sheet, choose page settings, and export a clean PDF without leaving the browser.',
    badge: 'XLSX',
    inputLabel: 'Choose one XLSX file or drop it here',
    inputHint: 'Best for worksheet-style tables and practical workbook exports',
    buttonLabel: 'Export PDF',
    processingLabel: 'Rendering PDF…',
    successLabel: 'Excel-to-PDF export',
    accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    validate: isXlsxFile,
  },
  'powerpoint-to-pdf': {
    title: 'Convert PPTX slides to PDF',
    subtitle: 'Export slide-by-slide PDF pages from a practical browser-side PPTX parse.',
    badge: 'PPTX',
    inputLabel: 'Choose one PPTX file or drop it here',
    inputHint: 'Supports practical text, images, and static slide content in v1',
    buttonLabel: 'Convert to PDF',
    processingLabel: 'Converting slides…',
    successLabel: 'PowerPoint-to-PDF conversion',
    accept: '.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation',
    validate: isPptxFile,
  },
};

function getOutputName(variant: OfficeToPdfVariant, fileName: string) {
  const baseName = getBaseName(fileName);

  switch (variant) {
    case 'word-to-pdf':
      return `${baseName}.pdf`;
    case 'excel-to-pdf':
      return `${baseName}-sheet.pdf`;
    case 'powerpoint-to-pdf':
      return `${baseName}.pdf`;
  }
}

export default function BrowserOfficeToPdf({ variant }: { variant: OfficeToPdfVariant }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const copy = toolCopy[variant];
  const [file, setFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [wordPreview, setWordPreview] = useState<WordPreview | null>(null);
  const [excelPreview, setExcelPreview] = useState<ExcelPreview | null>(null);
  const [pptPreview, setPptPreview] = useState<PowerPointPreview | null>(null);
  const [selectedSheetName, setSelectedSheetName] = useState('');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [pageSize, setPageSize] = useState<'a4' | 'letter'>('a4');

  const selectedSheet = useMemo(
    () =>
      excelPreview?.sheets.find((sheet) => sheet.name === selectedSheetName) ??
      excelPreview?.sheets[0] ??
      null,
    [excelPreview, selectedSheetName],
  );

  useEffect(() => {
    if (!excelPreview?.sheets.length) {
      return;
    }

    setSelectedSheetName((current) => current || excelPreview.sheets[0].name);
  }, [excelPreview]);

  function resetState() {
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setWordPreview(null);
    setExcelPreview(null);
    setPptPreview(null);
  }

  async function handleFileSelection(nextFile: File | null) {
    resetState();
    setFile(nextFile);

    if (!nextFile) {
      return;
    }

    if (!copy.validate(nextFile)) {
      setFile(null);
      setErrorMessage(copy.inputHint);
      return;
    }

    setIsLoading(true);

    try {
      if (variant === 'word-to-pdf') {
        const parsed = await readDocxHtml(nextFile);
        setWordPreview(parsed);
      } else if (variant === 'excel-to-pdf') {
        const workbook = await readWorkbookPreview(nextFile);
        setExcelPreview({ sheets: workbook.sheets });
      } else {
        const slides = await readPptxSlides(nextFile);
        setPptPreview({ slides });
      }
    } catch (error) {
      setFile(null);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'This Office file could not be read in the browser.',
      );
    } finally {
      setIsLoading(false);
    }
  }

  function clearFile() {
    setFile(null);
    resetState();
    setSelectedSheetName('');

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function runConversion() {
    if (!file) {
      setErrorMessage('Choose a file first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another Office-to-PDF export right now.',
      );

      let blob: Blob;

      if (variant === 'word-to-pdf') {
        const html = wordPreview?.html ?? (await readDocxHtml(file)).html;
        const blocks = parseWordHtmlToBlocks(html);
        blob = await exportWordBlocksToPdf(blocks);
      } else if (variant === 'excel-to-pdf') {
        if (!selectedSheet) {
          throw new Error('Pick a sheet first.');
        }
        blob = await exportSheetToPdf({
          rows: selectedSheet.rows,
          sheetName: selectedSheet.name,
          orientation,
          pageSize,
        });
      } else {
        const slides = pptPreview?.slides ?? (await readPptxSlides(file));
        if (!slides.length) {
          throw new Error('No readable slides were found in this PPTX file.');
        }
        blob = await exportSlidesToPdf(slides);
      }

      const url = URL.createObjectURL(blob);
      triggerBrowserDownload(
        url,
        createCleanorExportFileName(
          variant,
          [getOutputName(variant, file.name).replace(/\.pdf$/i, '')],
          'pdf',
        ),
      );
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setResultSize(blob.size);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The PDF export could not be completed.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="project-detail-card tool-live-card">
      <div className="tool-live-heading">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{file ? file.name : copy.inputLabel}</strong>
              <span>{file ? formatFileSize(file.size) : copy.inputHint}</span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={copy.accept}
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {variant === 'excel-to-pdf' && selectedSheet ? (
            <div className="tool-live-control-grid">
              <label className="tool-live-field">
                <span>Sheet</span>
                <select
                  onChange={(event) => setSelectedSheetName(event.target.value)}
                  value={selectedSheetName}
                >
                  {(excelPreview?.sheets ?? []).map((sheet) => (
                    <option key={sheet.name} value={sheet.name}>
                      {sheet.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="tool-live-field">
                <span>Page size</span>
                <select
                  onChange={(event) => setPageSize(event.target.value as 'a4' | 'letter')}
                  value={pageSize}
                >
                  <option value="a4">A4</option>
                  <option value="letter">Letter</option>
                </select>
              </label>
              <label className="tool-live-field">
                <span>Orientation</span>
                <select
                  onChange={(event) =>
                    setOrientation(event.target.value as 'portrait' | 'landscape')
                  }
                  value={orientation}
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </label>
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!file || isLoading || isProcessing}
              onClick={() => void runConversion()}
              type="button"
            >
              {isProcessing ? copy.processingLabel : copy.buttonLabel}
            </button>
            {file ? (
              <button className="button button-ghost" onClick={clearFile} type="button">
                Clear
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Export summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Source</strong>
              <span>{file ? file.name : 'No file selected'}</span>
            </div>
            <div>
              <strong>Size</strong>
              <span>{file ? formatFileSize(file.size) : '—'}</span>
            </div>
            <div>
              <strong>Output</strong>
              <span>PDF</span>
            </div>
            <div>
              <strong>Result</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>Practical Office fidelity</strong>
            <span>
              This browser-first export keeps the useful structure for common documents, but it is
              not meant to match full Microsoft Office rendering edge for edge.
            </span>
          </div>
        </aside>
      </div>

      {variant === 'word-to-pdf' && wordPreview ? (
        <div className="tool-live-preview">
          <h3>Document preview</h3>
          <div
            className="tool-live-rich-preview"
            dangerouslySetInnerHTML={{ __html: wordPreview.html }}
          />
          {wordPreview.messages.length ? (
            <div className="tool-live-panel-note">
              <strong>Conversion notes</strong>
              <span>{wordPreview.messages.join(' ')}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {variant === 'excel-to-pdf' && selectedSheet ? (
        <div className="tool-live-preview">
          <h3>Sheet preview</h3>
          <div
            className="tool-live-rich-preview"
            dangerouslySetInnerHTML={{ __html: buildSheetHtml(selectedSheet) }}
          />
        </div>
      ) : null}

      {variant === 'powerpoint-to-pdf' && pptPreview?.slides.length ? (
        <div className="tool-live-preview">
          <h3>Slide preview</h3>
          <div className="tool-live-results-grid">
            {pptPreview.slides.map((slide) => (
              <article className="tool-result-card" key={slide.id}>
                <strong>{slide.title}</strong>
                <span>{slide.textLines.slice(0, 4).join(' ') || 'Static slide export'}</span>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}
